/**
 * Font comparison preview — /preview/fonts
 *
 * Renders representative in-game copy (body paragraph, a player-stats line, an
 * event-log line, and small-size UI text) in the current body font plus a set
 * of candidate replacements, so the body font can be eyeballed for small-screen
 * readability.
 *
 * WEB-ORIENTED: the candidate families are pulled in via the Google-Fonts
 * @import in global.css. On native they'd need matching .ttf assets registered
 * in app/_layout — so on a device the unfamiliar families fall back until we
 * pick one and add its font file.
 */
import React from 'react'
import { Stack, useRouter } from 'expo-router'
import { View, Text, ScrollView, TouchableOpacity, Platform } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'

// Google-Fonts stylesheet for the candidates. Injected at runtime on web so
// loading doesn't depend on the global.css @import being picked up by the dev
// server / CSS pipeline.
const GOOGLE_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Courier+Prime:wght@400;700&family=Cutive+Mono&family=IBM+Plex+Mono:wght@400;600&family=Zilla+Slab:wght@400;600;700&family=Bitter:wght@400;600&family=Space+Mono:wght@400;700&family=JetBrains+Mono:wght@400;600&display=swap'

interface FontOption {
  name: string
  /** fontFamily value (must match the loaded family name). */
  family: string
  /** Generic fallback so the sample still shifts category if the font fails. */
  fallback: 'monospace' | 'serif' | 'sans-serif'
  note: string
  current?: boolean
}

const FONTS: FontOption[] = [
  { name: 'Zilla Slab', family: 'Zilla Slab', fallback: 'serif', note: 'Current — body font', current: true },
  { name: 'Courier Prime', family: 'Courier Prime', fallback: 'monospace', note: 'Legible Courier (mono)' },
  { name: 'Cutive Mono', family: 'Cutive Mono', fallback: 'monospace', note: 'Typewriter mono, cleaner/evener' },
  { name: 'IBM Plex Mono', family: 'IBM Plex Mono', fallback: 'monospace', note: 'Modern-clean mono with character' },
  { name: 'Space Mono', family: 'Space Mono', fallback: 'monospace', note: 'Retro mono, more character' },
  { name: 'JetBrains Mono', family: 'JetBrains Mono', fallback: 'monospace', note: 'Very clean mono (techy)' },
  { name: 'Bitter', family: 'Bitter', fallback: 'serif', note: 'Proportional slab, warm/readable' },
]

/** RN Web accepts a CSS font-family list; native uses the exact family name. */
function fontFamilyFor(f: FontOption): string {
  return Platform.OS === 'web' ? `"${f.family}", ${f.fallback}` : f.family
}

// Representative snippets pulled from real in-game surfaces.
const PARAGRAPH =
  'Reinforce your borders, then strike where the enemy is thinnest. Hold a whole region for its bonus, but watch the coasts — sea routes let raiders slip behind your lines.'
const STATS = '15 troops, 6 territories · cards: Warfish, Warfish, Blitzfin'
const LOG = 'Artemisia attacked Ontario from Quebec (3 vs 1) and conquered it.'
const SMALL = 'next reinforce: 3  ·  // includes +10 card bonus  ·  5t +3'

function Sample({ font }: { font: FontOption }) {
  return (
    <View
      className={`rounded-lg border-2 px-4 py-3 gap-2 ${
        font.current ? 'border-blood bg-blood/5' : 'border-olive bg-paper'
      }`}
    >
      <View className="flex-row items-baseline justify-between">
        {/* Label in a neutral font so the name is always legible. */}
        <Text className="text-ink font-stencil text-sm uppercase">{font.name}</Text>
        <Text className="text-ink-soft text-[10px] font-typed">{font.note}</Text>
      </View>

      <Text style={{ fontFamily: fontFamilyFor(font), fontSize: 16, lineHeight: 22 }} className="text-ink">
        {PARAGRAPH}
      </Text>
      <Text style={{ fontFamily: fontFamilyFor(font), fontSize: 14, lineHeight: 19 }} className="text-ink-soft">
        {STATS}
      </Text>
      <Text style={{ fontFamily: fontFamilyFor(font), fontSize: 13, lineHeight: 18 }} className="text-ink-soft">
        {LOG}
      </Text>
      <Text style={{ fontFamily: fontFamilyFor(font), fontSize: 11, lineHeight: 15 }} className="text-ink-soft">
        {SMALL}
      </Text>
    </View>
  )
}

export default function FontsPreviewScreen() {
  const router = useRouter()

  // Ensure the candidate fonts are actually loaded on web (independent of the
  // global.css @import / dev-server CSS reload).
  React.useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return
    const id = 'font-preview-google-fonts'
    if (document.getElementById(id)) return
    const link = document.createElement('link')
    link.id = id
    link.rel = 'stylesheet'
    link.href = GOOGLE_FONTS_HREF
    document.head.appendChild(link)
  }, [])

  return (
    <SafeAreaView className="flex-1 bg-canvas">
      <Stack.Screen options={{ headerShown: false }} />
      <View className="flex-row items-center px-4 py-3 border-b border-olive">
        <TouchableOpacity onPress={() => router.back()} accessibilityLabel="Go back" className="p-1 mr-2">
          <Text className="text-blood text-base font-typed">← Back</Text>
        </TouchableOpacity>
        <Text className="text-ink font-stencil text-xl flex-1 uppercase">Body font preview</Text>
      </View>
      <ScrollView contentContainerStyle={{ padding: 12, gap: 12 }}>
        {FONTS.map((f) => (
          <Sample key={f.name} font={f} />
        ))}
        <Text className="text-ink-soft font-typed text-[11px] text-center px-4 pt-2 pb-6">
          Web preview — on native, the chosen font also needs its .ttf added in
          app/_layout. Tell me which to wire in as the real body font.
        </Text>
      </ScrollView>
    </SafeAreaView>
  )
}
